import * as React from 'react'
import moment from 'moment'
import { Modal, Button, Skeleton } from 'antd'
import { parseAndDownloadCSV, GrPrice } from '../../../../../../utilities/helpers'
import { getCubeAPI } from '../../../../../../Cube'
import { QueryRenderer } from '@cubejs-client/react'
import { ReportFilterStore } from '../../ReportFilterStore'

interface Props {
  companyId: number
  categoryId: string
  nested: boolean // Refers to if parent or nested categories are shown
  reportFilterStore: ReportFilterStore
  onClose: () => void
}

const query = {
  measures: ['PaymentLines.productNet', 'PaymentLines.quantity'],
  dimensions: ['Products.fullname', 'Products.sku', 'Categories.fullname', 'Suppliers.name'],
  timeDimensions: [],
  filters: [
    {
      dimension: 'Payments.status',
      operator: 'equals',
      values: [`completed`],
    },
    {
      member: 'Products.fullname',
      operator: 'set',
    },
  ],
  limit: 10000,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}

export class CategoryTopProductsModal extends React.Component<Props> {
  data: any

  renderChart(Component: any) {
    return ({ resultSet, error }: any) => {
      if (resultSet) {
        this.data = resultSet.rawData()
      } else {
        this.data = undefined
      }
      return (
        (resultSet && <Component resultSet={resultSet} />) ||
        (error && error.toString()) || <Skeleton active />
      )
    }
  }

  tableRenderer = ({ resultSet }: any) => {
    const rawData: any[] = resultSet.rawData()

    if (rawData.length > 0) {
      return (
        <>
          <table className='table table-sm'>
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th className='text-right'>Qty</th>
                <th className='text-right'>Net</th>
              </tr>
            </thead>
            <tbody>
              {rawData.map((data, index) => {
                return (
                  <tr key={index}>
                    <td>{data['Products.fullname']}</td>
                    <td>{data['Products.sku']}</td>
                    <td className='text-right'>{data['PaymentLines.quantity']}</td>
                    <td className='text-right'>
                      {GrPrice.convertDollarToDollar(data['PaymentLines.productNet'])}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )
    } else {
      return <div>No data</div>
    }
  }

  export() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const data = this.data
    if (data) {
      parseAndDownloadCSV(
        `top-category-products-${moment(startDate).format('YYYY-MM-DD')}-${moment(endDate).format(
          'YYYY-MM-DD',
        )}`,
        data,
      )
    }
  }

  render() {
    const { companyId, nested } = this.props
    const { startDate, endDate, selectedLocationIds } = this.props.reportFilterStore!

    const fullQuery = {
      ...query,
      timeDimensions: [
        {
          dimension: 'Payments.completedate',
          granularity: null,
          dateRange: [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')],
        },
      ],
      filters: [
        ...query.filters,
        {
          dimension: nested ? 'Categories.id' : 'Categories.parentid',
          operator: 'equals',
          values: [`${this.props.categoryId}`],
        },
        {
          dimension: 'Locations.id',
          operator: 'equals',
          values: selectedLocationIds.map((id) => `${id}`),
        },
      ],
    }

    return (
      <Modal
        title='Top selling products'
        visible={true}
        onCancel={this.props.onClose}
        width={700}
        footer={<Button onClick={this.props.onClose}>Cancel</Button>}
      >
        <div className='table-responsive'>
          <div className='mb-2'>
            <Button onClick={() => this.export()}>Export as csv</Button>
          </div>

          <QueryRenderer
            query={fullQuery}
            cubejsApi={getCubeAPI(companyId)}
            render={this.renderChart(this.tableRenderer)}
          />
        </div>
      </Modal>
    )
  }
}
